import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Text, WarningLabel,
} from '@common-fe/common-fe';

import { StatusWrapper } from '@/components';
import { WarnModal } from '@/components/elements';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { EmployerStatus } from '@/modules/employer/employer.constants';

import { PlanCardAction } from '../../plan.types';
import PlanModalWrapper from '../PlanModalWrapper';
import { useForceOpenModalStore } from '../stores';

import type { PlanTemplateFormRef } from './PlanTemplate/PlanTemplateForm';
import PlanTemplateForm from './PlanTemplate/PlanTemplateForm';
import Footer from './Footer';
import { useCreatePlanTemplate } from './hooks';

interface Props {
  hide?: boolean;
}

const CreateTemplateModal: React.FC<Props> = ({ hide }) => {
  const modalStore = useForceOpenModalStore((state) => state.state);
  const handleClearModalStore = useForceOpenModalStore((state) => state.clear);
  const [modalVisible, setModalVisible] = useState(false);
  const formRef = useRef<PlanTemplateFormRef | null>(null);
  // const handleCheck = useCheckPaymentOrder();
  const handleCancel = useCallback(() => {
    setModalVisible(false);
    handleClearModalStore();
  }, [handleClearModalStore]);

  useEffect(() => {
    if (modalStore.actionType === PlanCardAction.createTemplateFromTemplate && modalStore.id) {
      setModalVisible(true);
    }
  }, [modalStore.actionType, modalStore.id]);

  const {
    handleSave,
    loading,
    setLoading,
    handleReset,
    planCreationError,
    setPlanCreationError,
  } = useCreatePlanTemplate(handleCancel);
  useEffect(() => {
    if (!modalVisible) {
      handleReset();
    }
  }, [handleReset, modalVisible]);
  const handleSubmit = useCallback(async () => {
    if (!formRef.current) return;
    const isValid = formRef.current.handlePreSubmit();
    if (isValid) {
      const formValues = formRef.current.getValues();
      try {
        setLoading(true);
        await handleSave(formValues);
      } catch {
        setLoading(false);
        handleCancel();
      }
    }
  }, [handleSave, setLoading, handleCancel]);
  const handleToggleModal = useCallback(() => {
    const newValue = !modalVisible;
    setModalVisible(newValue);
    handleReset();
    if (!newValue) {
      handleClearModalStore();
    }
  }, [handleClearModalStore, handleReset, modalVisible]);

  const { observingOrganization } = useCurrentOrganization();
  const isTerminationStatus = useMemo(() => observingOrganization.status === EmployerStatus.PendingTermination
  || observingOrganization.status === EmployerStatus.TerminationInProgress
  || observingOrganization.status === EmployerStatus.Terminated, [observingOrganization]);

  return (
    <>
      <PlanModalWrapper
        visible={modalVisible}
        testId="CreateTemplate"
        onSetVisible={handleToggleModal}
        hide={hide}
        buttonText="Create Plan Template"
        modalTitle="Create Plan Template"
        disabled={isTerminationStatus}
        hintText={isTerminationStatus ? 'Terminated organizations cannot create plan template.' : undefined}
      >
        <WarnModal
          visible={planCreationError}
          testId="CreateTemplate_payment_order"
          onSetVisible={setPlanCreationError}
          header="The server encountered an error processing the request"
          helptext={`It doesn't appear to have affected your data, but we cannot save the plan template with the latest records.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
          buttonText="Close"
          onSubmit={handleSave}
          submitButtonText="Try Again"
        />
        <Box>
          <Box dir="column" background="module" pad="spacing24" round="moduleRound">
            <PlanTemplateForm
              testId="CreateTemplate"
              parentId={modalStore.id}
              formRef={formRef}
            />

            <Box margin={{ top: 'spacing24' }}>
              <StatusWrapper status="pending">
                <Box
                  margin="spacing12"
                  pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
                  background={{ color: 'warningContainer' }}
                  border={{ size: '1px', color: 'warningBorder' }}
                  round="container2Round"
                >
                  <Text weight="bold" margin={{ bottom: 'spacing8' }} color="textBody">
                    <WarningLabel
                      data-testid="CreateTemplate_warning_message"
                    >
                      Does everything look good? Please double check your selections.
                    </WarningLabel>
                  </Text>

                  <Box>
                    <Text
                      data-testid="CreateTemplate_confirm_message"
                      margin={{ bottom: 'spacing8' }}
                      color="textBody"
                      size="14px"
                    >
                      Once created, you can’t change the plan template or plan year.
                    </Text>
                  </Box>
                </Box>
              </StatusWrapper>
            </Box>
          </Box>
          <Footer
            testId="CreateTemplate_footer"
            loading={loading}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </Box>

      </PlanModalWrapper>
    </>
  );
};
export default CreateTemplateModal;
